import { useState, useEffect, useRef } from "react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { useSelector } from "react-redux";

const geoUrl = "/features.json";

const MapChart = ({ zoom, setZoom, searchResults, onSearchSelect }) => {
  const [tooltip, setTooltip] = useState({
    show: false,
    content: "",
    coords: [0, 0],
  });
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: zoom });
  const [activeMarker, setActiveMarker] = useState(null);
  const mapRef = useRef(null);
  const audienceDetails = useSelector((state) => state.dataanalyst);
  const { geoDetails } = audienceDetails;
  const geoData = geoDetails?.length > 0 ? geoDetails : [];
  const projectionRef = useRef();

  useEffect(() => {
    setPosition((pos) => ({ ...pos, zoom }));
  }, [zoom]);

  // Listen for search selection from parent
  useEffect(() => {
    const handleSearchSelect = (e) => {
      const button = e.target.closest('[data-location]');
      if (button) {
        const location = JSON.parse(button.dataset.location);
        handleLocationSelect(location);
      }
    };

    document.addEventListener('click', handleSearchSelect);
    return () => document.removeEventListener('click', handleSearchSelect);
  }, []);

  // Add wheel event listener for trackpad/mousewheel zoom
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      
      if (e.deltaY < 0) {
        // Zoom in
        if (position.zoom < 16) {
          setPosition((pos) => {
            const newZoom = pos.zoom * 1.5;
            setZoom(newZoom);
            return { ...pos, zoom: newZoom };
          });
        }
      } else {
        // Zoom out
        if (position.zoom > 1) {
          setPosition((pos) => {
            const newZoom = pos.zoom / 1.5;
            setZoom(newZoom);
            return { ...pos, zoom: newZoom };
          });
        }
      }
    };

    const mapElement = mapRef.current;
    if (mapElement) {
      mapElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (mapElement) {
        mapElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [position.zoom, setZoom]);

  const handleMarkerEvent = (marker) => {
    const proj = projectionRef.current;
    if (proj) {
      const [x, y] = proj([marker.longitude, marker.latitude]);
      setActiveMarker(marker);
      setTooltip({
        show: true,
        content: `${marker.name}: ${marker.value}`,
        coords: [x, y],
      });
    }
  };

  const handleMoveEnd = (position) => {
    setPosition(position);
    if (activeMarker && projectionRef.current) {
      const [x, y] = projectionRef.current([
        activeMarker.longitude,
        activeMarker.latitude,
      ]);
      setTooltip((prev) => ({
        ...prev,
        coords: [x, y],
      }));
    }
  };

  useEffect(() => {
    if (activeMarker && projectionRef.current) {
      const [x, y] = projectionRef.current([
        activeMarker.longitude,
        activeMarker.latitude,
      ]);
      setTooltip((prev) => ({
        ...prev,
        coords: [x, y],
      }));
    }
  }, [activeMarker, position.zoom]);

  const maxVal = Math.max(...geoData?.map((d) => d.peak_listeners));
  const scale = (value) => Math.sqrt(value / maxVal) * (25 / (position.zoom || 1));

  const getColor = (value) => {
    if (value > 240) return "rgba(255, 69, 0, 0.7)";
    if (value > 200) return "rgba(255, 99, 71, 0.7)";
    if (value > 100) return "rgba(255, 140, 0, 0.7)";
    if (value > 50)  return "rgba(255, 165, 0, 0.7)";
    return "rgba(255, 200, 124, 0.7)"; 
  };

  const handleLocationSelect = (location) => {
    // Zoom to the selected location
    setPosition({
      coordinates: [location.longitude, location.latitude],
      zoom: 200,
    });
    setZoom(200);
    
    // Show tooltip for the selected location
    setActiveMarker(location);
    const proj = projectionRef.current;
    if (proj) {
      const [x, y] = proj([location.longitude, location.latitude]);
      setTooltip({
        show: true,
        content: `${location.name}
         Peak Listeners: ${location.peak_listeners || 0}
         Current Listeners: ${location.current_listeners || 0}`,
        coords: [x, y],
      });
    }
    
    // Notify parent
    if (onSearchSelect) {
      onSearchSelect(location);
    }
  };

  return (
    <div className="relative" ref={mapRef}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 75 }}
      >
        <ZoomableGroup
          zoom={position.zoom}
          center={position.coordinates}
          onMoveStart={() => {
            setTooltip((prev) => ({ ...prev, show: false }));
          }}
          onMoveEnd={handleMoveEnd}
          maxZoom={600}
        >
          <Geographies geography={geoUrl}>
            {({ geographies, projection }) => {
              projectionRef.current = projection;
              return geographies.map((geo) => (
                <Geography
                  fill={"#FFFFFF"}
                  stroke="#fe3d00"
                  key={geo.rsmKey}
                  geography={geo}
                />
              ));
            }}
          </Geographies>

          {(geoData || [])?.map((marker, index) => (
            <Marker
              key={index}
              coordinates={[marker.longitude, marker.latitude]}
              onMouseEnter={() => {
                handleMarkerEvent(marker);
                const proj = projectionRef.current;
                if (proj) {
                  const [x, y] = proj([marker.longitude, marker.latitude]);
                  setTooltip({
                    show: true,
                    content: `${marker.name}
                     Peak Listeners: ${marker.peak_listeners || 0}
                     Current Listeners: ${marker.current_listeners || 0}`,
                    coords: [x, y],
                  });
                }
              }}
              onMouseLeave={() => setTooltip({ ...tooltip, show: false })}
              onClick={() => {
                setActiveMarker(null);
                setTooltip((prev) => ({ ...prev, show: false }));
                const proj = projectionRef.current;
                if (proj) {
                  const [x, y] = proj([marker.longitude, marker.latitude]);
                  setTooltip({
                    show: true,
                    content: `${marker.name}
                    Peak Listeners: ${marker.peak_listeners || 0}
                    Current Listeners: ${marker.current_listeners || 0}`,
                    coords: [x, y],
                  });
                }
              }}
            >
              <circle
                r={scale(marker.peak_listeners) / Math.sqrt(position.zoom || 1)}
                fill={getColor(marker.peak_listeners)}
                stroke="#333"
                strokeWidth={1}
              />
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {tooltip.show && (
        <div
          className="absolute bg-white px-2 py-1 rounded shadow-lg text-sm text-gray-500 pointer-events-none break-normal z-50 transition-all duration-200"
          style={{
            left: `${tooltip.coords[0]}px`,
            top: `${tooltip.coords[1]}px`,
            transform: "translate(-50%, -50%)",
            minWidth: 'auto',
            textAlign: "center",
          }}
        >
          {tooltip.content.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapChart;